/**
 * Custom-product category registry — frontend mirror.
 *
 * This is the form-driving view of the backend's code-controlled registry
 * (backend/src/catalog/category-registry.ts). Slugs MUST stay identical to the
 * backend so posted `category`/`facets` validate server-side. Only the fields
 * the upload form needs live here (label, group, brand/collection labels,
 * facets). Tags / Shopify mapping are computed server-side from the slug.
 *
 * Both apps deploy together, so a static mirror is acceptable; if drift becomes
 * a concern, expose the registry from the API and load it here instead.
 */

export type CategoryGroup = 'TCG' | 'Models' | 'Storage' | 'Play' | 'Merch' | 'Events'

export type FacetType = 'text' | 'int' | 'bool' | 'select' | 'multitext'

export interface CategoryFacet {
  key: string
  label: string
  type?: FacetType // defaults to 'text'
  options?: string[] // for 'select'
  /**
   * Suggestions offered for a 'multitext' facet. Free typing still works — the
   * list only exists so the same colour is spelled the same way every time.
   */
  suggestions?: string[]
  placeholder?: string
  /** Shown under the field. Use it to explain what leaving it blank does. */
  hint?: string
}

/**
 * Offered on every colour facet. A binder comes in a dozen colours and the
 * seller stocks all of them, so the field takes a list — one product, every
 * colour it ships in — rather than forcing a separate entry per colour.
 */
export const COMMON_COLOURS = [
  'Black', 'White', 'Red', 'Blue', 'Green', 'Yellow', 'Orange', 'Purple',
  'Pink', 'Grey', 'Brown', 'Gold', 'Silver', 'Clear', 'Multicolour',
]

export interface CustomCategory {
  slug: string
  label: string
  group: CategoryGroup
  /**
   * Label for the brand/line field. Omit it and the field is not rendered at
   * all — the form only ever shows the axes a category actually uses.
   */
  brandLabel?: string
  /** When set, a collection/grouping field is shown with this label. */
  collectionLabel?: string
  facets?: CategoryFacet[]
  /**
   * The brand axis is a TCG game, so the field offers the games already in the
   * catalog. Free typing still works — the list only exists so the same game is
   * spelled the same way every time and groups correctly on the storefront.
   */
  brandIsGame?: boolean
  /**
   * The product can be sold ahead of a release date. Sealed TCG product and
   * merch ship on a street date; sleeves off a shelf do not.
   */
  supportsPreorder?: boolean
  /** Marks a ticket/entry product: no stock to weigh or scan, and the listing
   *  end date is the moment the event cuts off rather than a shelf-life. */
  isEvent?: boolean
  /** Label for the optional listing end date, when "Stop selling" is too vague. */
  endDateLabel?: string
  /**
   * The collection axis is a TCG set, so the field offers the sets already in
   * the catalog for the chosen game. Same deal as {@link brandIsGame}: free
   * typing works, the list just makes the spelling match.
   */
  collectionIsSet?: boolean
  /** Label for the stock field, when "Quantity" is the wrong word. */
  quantityLabel?: string
  /** Explanation shown under the stock field. */
  quantityHint?: string
  /** Label for the price field, when "Price" is the wrong word. */
  priceLabel?: string
  /** Whether a cost basis makes sense. An event ticket has no cost of goods. */
  supportsCostBasis?: boolean
  /** One-line explanation shown under the category picker. */
  hint?: string
  /**
   * Override for whether the product is a physical thing that gets posted and
   * scanned. Defaults to "anything that is not an event" — see
   * {@link isPhysicalCategory} — so only an exception has to say so.
   */
  isPhysicalGoods?: boolean
}


export const CUSTOM_CATEGORIES: CustomCategory[] = [
  // ---- TCG (manual sealed entry; singles come from the card catalog) --------
  { slug: 'sealed_booster', label: 'Booster Pack', group: 'TCG', brandLabel: 'Game', brandIsGame: true, collectionLabel: 'Set', collectionIsSet: true, supportsPreorder: true },
  { slug: 'booster_box', label: 'Booster Box', group: 'TCG', brandLabel: 'Game', brandIsGame: true, collectionLabel: 'Set', collectionIsSet: true, supportsPreorder: true },
  { slug: 'etb', label: 'Elite Trainer Box', group: 'TCG', brandLabel: 'Game', brandIsGame: true, collectionLabel: 'Set', collectionIsSet: true, supportsPreorder: true },
  { slug: 'bundle', label: 'Bundle', group: 'TCG', brandLabel: 'Game', brandIsGame: true, collectionLabel: 'Set', collectionIsSet: true, supportsPreorder: true },
  { slug: 'starter_deck', label: 'Starter Deck', group: 'TCG', brandLabel: 'Game', brandIsGame: true, collectionLabel: 'Set', collectionIsSet: true, supportsPreorder: true },
  { slug: 'promo', label: 'Promo', group: 'TCG', brandLabel: 'Game', brandIsGame: true, collectionLabel: 'Set', collectionIsSet: true, supportsPreorder: true },
  { slug: 'sealed_other', label: 'Other Sealed Product', group: 'TCG', brandLabel: 'Game', brandIsGame: true, collectionLabel: 'Set', collectionIsSet: true, supportsPreorder: true },

  // ---- Models & Hobby -------------------------------------------------------
  {
    slug: 'figurine', label: 'Figurine', group: 'Models', brandLabel: 'Brand',
    collectionLabel: 'Faction / Range',
    facets: [
      { key: 'scale', label: 'Scale' },
      { key: 'material', label: 'Material' },
      { key: 'painted', label: 'Painted', type: 'bool' },
    ],
  },
  {
    slug: 'model_kit', label: 'Model Kit', group: 'Models', brandLabel: 'Brand',
    collectionLabel: 'Faction / Range',
    facets: [{ key: 'pieceCount', label: 'Piece count', type: 'int' }],
  },
  {
    slug: 'paint', label: 'Paint', group: 'Models', brandLabel: 'Brand',
    facets: [
      { key: 'colour', label: 'Colour' },
      { key: 'volumeMl', label: 'Volume (ml)', type: 'int' },
    ],
  },
  {
    slug: 'hobby_tool', label: 'Hobby Tool', group: 'Models', brandLabel: 'Brand',
    facets: [{ key: 'material', label: 'Material' }],
  },

  // ---- Storage & Protection -------------------------------------------------
  {
    slug: 'binder', label: 'Binder', group: 'Storage', brandLabel: 'Brand',
    facets: [
      { key: 'capacity', label: 'Capacity (cards)', type: 'int' },
      { key: 'colour', label: 'Colours', type: 'multitext', suggestions: COMMON_COLOURS },
    ],
  },
  {
    slug: 'card_sleeves', label: 'Card Sleeves', group: 'Storage', brandLabel: 'Brand',
    facets: [
      { key: 'size', label: 'Size', type: 'select', options: ['Standard', 'Japanese', 'Oversized'] },
      { key: 'colour', label: 'Colours', type: 'multitext', suggestions: COMMON_COLOURS },
      { key: 'count', label: 'Pack count', type: 'int' },
    ],
  },
  {
    slug: 'toploader', label: 'Toploader', group: 'Storage', brandLabel: 'Brand',
    facets: [{ key: 'count', label: 'Pack count', type: 'int' }],
  },
  {
    slug: 'deck_box', label: 'Deck Box', group: 'Storage', brandLabel: 'Brand',
    facets: [
      { key: 'capacity', label: 'Capacity (cards)', type: 'int' },
      { key: 'colour', label: 'Colours', type: 'multitext', suggestions: COMMON_COLOURS },
    ],
  },
  {
    slug: 'storage_box', label: 'Storage Box', group: 'Storage', brandLabel: 'Brand',
    facets: [{ key: 'capacity', label: 'Capacity (cards)', type: 'int' }],
  },
  {
    slug: 'acrylic_box', label: 'Acrylic Box', group: 'Storage', brandLabel: 'Brand',
    facets: [
      { key: 'capacity', label: 'Capacity (cards)', type: 'int' },
      { key: 'dimensions', label: 'Dimensions' },
    ],
  },
  {
    slug: 'display_case', label: 'Display Case', group: 'Storage', brandLabel: 'Brand',
    facets: [
      { key: 'dimensions', label: 'Dimensions' },
      { key: 'material', label: 'Material' },
    ],
  },

  // ---- Play -----------------------------------------------------------------
  {
    slug: 'playmat', label: 'Playmat', group: 'Play', brandLabel: 'Brand',
    facets: [{ key: 'dimensions', label: 'Dimensions' }],
  },
  {
    slug: 'dice', label: 'Dice', group: 'Play', brandLabel: 'Brand',
    facets: [
      { key: 'count', label: 'Count', type: 'int' },
      { key: 'colour', label: 'Colours', type: 'multitext', suggestions: COMMON_COLOURS },
    ],
  },
  {
    slug: 'tokens', label: 'Tokens', group: 'Play', brandLabel: 'Brand',
    facets: [
      { key: 'count', label: 'Count', type: 'int' },
      { key: 'material', label: 'Material' },
    ],
  },
  {
    slug: 'game_mat', label: 'Game Mat', group: 'Play', brandLabel: 'Brand',
    facets: [{ key: 'dimensions', label: 'Dimensions' }],
  },

  // ---- Merch ----------------------------------------------------------------
  { slug: 'plushie', label: 'Plushie', group: 'Merch', brandLabel: 'Brand', collectionLabel: 'Series', supportsPreorder: true },
  { slug: 'toy', label: 'Toy', group: 'Merch', brandLabel: 'Brand', collectionLabel: 'Series', supportsPreorder: true },
  {
    slug: 'apparel', label: 'Apparel', group: 'Merch', brandLabel: 'Brand',
    facets: [
      { key: 'size', label: 'Size', type: 'select', options: ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', 'One size'] },
      { key: 'colour', label: 'Colours', type: 'multitext', suggestions: COMMON_COLOURS },
      { key: 'material', label: 'Material' },
    ],
  },
  {
    slug: 'keychain', label: 'Keychain', group: 'Merch', brandLabel: 'Brand',
    facets: [{ key: 'material', label: 'Material' }],
  },
  {
    slug: 'pin', label: 'Pin', group: 'Merch', brandLabel: 'Brand',
    facets: [{ key: 'material', label: 'Material' }],
  },

  // ---- Events ---------------------------------------------------------------
  {
    slug: 'event', label: 'Event / Ticket', group: 'Events',
    // A game night is for a game, so the brand axis is still useful — it is what
    // groups the listing with the rest of that game on the storefront.
    brandLabel: 'Game / System', brandIsGame: true,
    isEvent: true,
    // "Places available" sat directly under "Location" and read as a list of
    // venues. Tickets is the word nobody misreads.
    quantityLabel: 'Tickets available',
    quantityHint: 'How many people can buy in. The listing sells out when they are gone.',
    priceLabel: 'Entry fee',
    endDateLabel: 'Event date — entry closes',
    supportsCostBasis: false,
    hint: 'Game nights, release nights, tournaments. Set an end date and the listing removes itself when the event is over.',
    facets: [
      { key: 'eventType', label: 'Event type', type: 'select', options: ['Game Night', 'Release Night', 'Tournament', 'Prerelease', 'League', 'Other'] },
      {
        key: 'location',
        label: 'Location',
        placeholder: 'Leave blank — we’ll use your store address',
        hint: 'Almost always your own shop. Only fill this in when the event is somewhere else.',
      },
    ],
  },

  // ---- Catch-all ------------------------------------------------------------
  { slug: 'accessory', label: 'Other Accessory', group: 'Merch', brandLabel: 'Brand' },
]

export const CATEGORY_GROUP_ORDER: CategoryGroup[] = ['TCG', 'Models', 'Storage', 'Play', 'Merch', 'Events']

/**
 * Does this category describe something that is boxed, weighed and scanned?
 *
 * Physical goods get a shipping weight and a barcode on the form; an event
 * ticket is neither posted nor pulled off a shelf, so asking for either would
 * be noise.
 */
export function isPhysicalCategory(category: CustomCategory): boolean {
  return category.isPhysicalGoods ?? !category.isEvent
}

export function getCustomCategory(slug: string): CustomCategory | undefined {
  return CUSTOM_CATEGORIES.find((c) => c.slug === slug)
}

/** Categories grouped for an optgroup-style picker, in canonical group order. */
export function customCategoriesByGroup(): Array<{ group: CategoryGroup; categories: CustomCategory[] }> {
  return CATEGORY_GROUP_ORDER.map((group) => ({
    group,
    categories: CUSTOM_CATEGORIES.filter((c) => c.group === group),
  })).filter((g) => g.categories.length > 0)
}
